
import React, { useState, useEffect } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Category {
  id: string;
  name: string;
  square_category_id?: string;
}

interface SearchableCategorySelectorProps {
  categories: Category[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  categorySource?: string; // Add this to identify AI categories
}

export const SearchableCategorySelector = ({ 
  categories, 
  value, 
  onValueChange, 
  placeholder = "Select category...",
  categorySource
}: SearchableCategorySelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Find category by direct ID match (master categories) or by square_category_id (legacy Square IDs)
  const selectedCategory = categories?.find(c => c.id === value) || 
    categories?.find(c => c.square_category_id === value);

  // Enhanced debugging
  console.log('SearchableCategorySelector render:', {
    categories: categories?.length || 0,
    value,
    selectedCategory: selectedCategory?.name || 'Not found',
    isSquareId: value && !categories?.find(c => c.id === value) && categories?.find(c => c.square_category_id === value) ? true : false
  });

  // Filter categories based on search
  const filteredCategories = categories?.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // selectedCategory already defined above with proper fallback logic

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            categorySource === 'ai' && "border-purple-500 border-2 shadow-lg shadow-purple-200/50"
          )}
        >
          <span className="truncate">
            {selectedCategory ? selectedCategory.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 shadow-lg z-50">
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[200px] overflow-auto">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              No categories found.
            </CommandEmpty>
            <CommandGroup>
              {filteredCategories.map((category) => (
                <CommandItem
                  key={category.id}
                  value={category.id}
                  onSelect={() => {
                    console.log('🎯 Category selected:', { id: category.id, name: category.name });
                    onValueChange(category.id);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer px-3 py-2"
                >
                   <Check
                     className={cn(
                       "mr-2 h-4 w-4",
                       (value === category.id || value === category.square_category_id) ? "opacity-100" : "opacity-0"
                     )}
                   />
                  <span className="truncate">{category.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
